/**
 * @file useShopDomainActions.ts
 * @description Действия вкладки «Домены» (phase19.28 этап А;
 * uiux-platform-shops §5, §7; PL10–PL12, SEO4, В11): проверить,
 * сделать главным, отключить/включить, удалить. Подтверждения — окном
 * кабинета с текстами дизайнера; после каждой мутации карточка магазина
 * перечитывается (домены живут в ней — один источник правды).
 *
 * Отказ проверки не стирает прошлый результат: под списком — полоса
 * «Не удалось проверить… Ниже — результат от …» или «Повторить можно через N с».
 */

import { hostToUnicode } from "@chaiberi/shared";
import { useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { useConfirm } from "../../components/ui/use-confirm";
import { platformShops, type ShopDomain } from "../../lib/api-platform-shops";
import { domainStatusMeta } from "../../lib/platform/shop-domain-status";
import { domainActionError, recheckErrorText } from "../../lib/platform/shop-errors";

const shown = (d: ShopDomain) => d.hostUnicode || hostToUnicode(d.host);

const MAKE_PRIMARY_TEXT = (host: string) =>
  `Остальные адреса будут отдавать 301 на ${host} с сохранением пути; canonical и карта сайта перейдут на ${host}. ` +
  "Поисковикам нужно от нескольких дней до недель. В Яндекс Вебмастере оформите «Переезд сайта», в Google — «Изменение адреса». " +
  "Покупателям придётся заново войти в личный кабинет. Не меняйте главный адрес часто.";

const DROP_TEXT = "Адрес перестанет открывать магазин: посетители увидят «Домен не подключён». Хост в NPM удалите вручную.";

export function useShopDomainActions(shopId: string, reload: () => void) {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<{ domainId: string; text: string } | null>(null);
  /** Объявление для программ чтения экрана (aria-live). */
  const [announce, setAnnounce] = useState("");

  async function recheck(domain: ShopDomain) {
    setCheckingId(domain.id);
    setCheckError(null);
    try {
      const fresh = await platformShops.recheck(shopId, domain.id);
      setAnnounce(`${shown(domain)}: ${domainStatusMeta(fresh).label}`);
      reload();
    } catch (err) {
      const text = recheckErrorText(err, domain.lastCheckAt);
      setCheckError({ domainId: domain.id, text });
      setAnnounce(`${shown(domain)}: ${text}`);
    } finally {
      setCheckingId(null);
    }
  }

  async function run(action: () => Promise<unknown>, success: string, failure: string) {
    try {
      await action();
      notify.success(success);
      reload();
    } catch (err) {
      notify.error(domainActionError(err, failure));
      // Отказ мог быть из-за чужой правки (409 «основной адрес сменили») — список перечитываем.
      reload();
    }
  }

  async function makePrimary(domain: ShopDomain) {
    const host = shown(domain);
    const ok = await confirmApi.confirm({ title: `Сделать главным ${host}?`, description: MAKE_PRIMARY_TEXT(host), action: "Сделать главным" });
    if (ok) await run(() => platformShops.makePrimary(shopId, domain.id), `Главный адрес — ${host}`, "Не удалось сменить главный адрес");
  }

  async function toggle(domain: ShopDomain) {
    const disable = domain.status === "ACTIVE";
    if (disable) {
      const ok = await confirmApi.confirm({ title: `Отключить ${shown(domain)}?`, description: DROP_TEXT, action: "Отключить", danger: true });
      if (!ok) return;
    }
    await run(
      () => platformShops.updateDomain(shopId, domain.id, { status: disable ? "DISABLED" : "ACTIVE" }),
      disable ? "Адрес отключён" : "Адрес включён",
      "Не удалось изменить адрес",
    );
  }

  async function remove(domain: ShopDomain) {
    const ok = await confirmApi.confirm({ title: `Удалить ${shown(domain)}?`, description: DROP_TEXT, action: "Удалить", danger: true });
    if (ok) await run(() => platformShops.removeDomain(shopId, domain.id), "Адрес удалён", "Не удалось удалить адрес");
  }

  return { confirmApi, checkingId, checkError, announce, recheck, makePrimary, toggle, remove };
}
